import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import logoMark from "@/assets/smg-logo-mark.png";

const LINKS = [
  { label: "Players", href: "#gallery" },
  { label: "News", href: "#news" },
  { label: "Guides", href: "/guides" },
  { label: "Lineage", href: "#lineage" },
  { label: "Difference", href: "#difference" },
  { label: "Services", href: "#services" },
  { label: "Team", href: "#team" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "border-b border-white/10 bg-ink/85 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-3 sm:px-12 lg:px-20">
          <a href="#top" data-testid="nav-logo-link" className="flex items-center gap-3">
            <span className="relative inline-block">
              <img
                src={logoMark}
                alt="SMG Sports Management Group International"
                className="h-12 w-auto sm:h-16"
                data-testid="nav-logo"
              />
              <span
                className="absolute -right-3 -top-1 font-mono text-[9px] text-slate-400 sm:text-[10px]"
                data-testid="nav-logo-tm"
              >
                ™
              </span>
            </span>
          </a>

          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) =>
              l.href.startsWith("/") ? (
                <Link
                  key={l.href}
                  to={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 transition-colors hover:text-volt"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 transition-colors hover:text-volt"
                >
                  {l.label}
                </a>
              )
            )}
            <a
              href="#apply"
              data-testid="nav-apply-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-volt px-5 py-2.5 font-display text-sm uppercase tracking-wider text-ink transition-colors hover:bg-volt-light"
            >
              Apply Now
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </nav>

          <button
            className="inline-flex items-center justify-center rounded-lg border border-white/10 p-2.5 text-white lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[90] flex flex-col justify-center bg-ink/98 px-8 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <nav className="flex flex-col gap-6">
              {LINKS.map((l, i) =>
                l.href.startsWith("/") ? (
                  <motion.div
                    key={l.href}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i, duration: 0.4 }}
                  >
                    <Link
                      to={l.href}
                      onClick={() => setOpen(false)}
                      data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                      className="font-display text-4xl uppercase text-white transition-colors hover:text-volt"
                    >
                      {l.label}
                    </Link>
                  </motion.div>
                ) : (
                  <motion.a
                    key={l.href}
                    href={l.href}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i, duration: 0.4 }}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className="font-display text-4xl uppercase text-white transition-colors hover:text-volt"
                  >
                    {l.label}
                  </motion.a>
                )
              )}
              <motion.a
                href="#apply"
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-apply-cta"
                className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-volt px-8 py-4 font-display text-lg uppercase text-ink"
              >
                Apply Now <ArrowUpRight className="h-5 w-5" />
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
